import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, Gift, Copy, Loader2, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientReferralsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/client/referrals`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyCode = () => {
    if (data?.referral_code) {
      navigator.clipboard.writeText(data.referral_code);
      toast.success('Codigo copiado al portapapeles');
    }
  };

  const redeemPoints = async () => {
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/client/referrals/redeem`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al canjear puntos');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="client-referrals-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Referidos</h1>
        <p className="text-slate-500">Invita amigos y gana puntos</p>
      </div>

      {/* Referral Code Card */}
      <Card className="p-6 bg-gradient-to-br from-violet-600 to-violet-800 text-white border-0">
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-8 h-8" />
          <span className="text-violet-200 text-lg">Tu codigo de referido</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-white/20 rounded-xl px-4 py-3 font-mono text-xl font-bold tracking-widest" data-testid="referral-code">
            {data?.referral_code || 'N/A'}
          </div>
          <Button onClick={copyCode} variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" data-testid="copy-referral-btn">
            <Copy className="w-5 h-5" />
          </Button>
        </div>
        <p className="text-violet-200 text-sm mt-3">Comparte este codigo y gana 50 puntos por cada referido</p>
      </Card>

      {/* Points */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Award className="w-6 h-6 text-amber-500" />
            <h3 className="font-semibold text-slate-900">Tus puntos</h3>
          </div>
          <p className="text-3xl font-bold text-slate-900" data-testid="total-points">{data?.total_points || 0}</p>
          <p className="text-sm text-slate-500 mt-1">200 puntos = $5.00 descuento</p>
          <Button onClick={redeemPoints} disabled={redeeming || (data?.total_points || 0) < 200} className="mt-3 bg-amber-500 hover:bg-amber-600" data-testid="redeem-btn">
            {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Canjear cupon'}
          </Button>
        </Card>

        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6 text-violet-500" />
            <h3 className="font-semibold text-slate-900">Referidos totales</h3>
          </div>
          <p className="text-3xl font-bold text-slate-900" data-testid="referral-count">{data?.referrals?.length || 0}</p>
        </Card>
      </div>

      {/* Coupons */}
      {data?.coupons?.length > 0 && (
        <Card className="p-6 border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Gift className="w-5 h-5 text-emerald-500" /> Tus cupones</h3>
          <div className="space-y-2">
            {data.coupons.map((c) => (
              <div key={c.id} className={`p-3 rounded-lg border ${c.is_used ? 'bg-slate-50 opacity-60' : 'bg-emerald-50 border-emerald-200'}`} data-testid={`coupon-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono font-bold text-slate-900">{c.code}</p>
                    <p className="text-sm text-slate-500">Descuento: ${c.discount_amount?.toFixed(2)}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${c.is_used ? 'bg-slate-200 text-slate-500' : 'bg-emerald-200 text-emerald-700'}`}>
                    {c.is_used ? 'Usado' : 'Disponible'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Referral History */}
      {data?.referrals?.length > 0 && (
        <Card className="p-6 border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Historial de referidos</h3>
          <div className="space-y-2">
            {data.referrals.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm text-slate-900">{r.referred_name}</p>
                  <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString('es-ES')}</p>
                </div>
                <span className="text-sm font-semibold text-emerald-600">+{r.points_earned} pts</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ClientReferralsPage;
